import React, { useEffect, useRef, useState } from "react";
import { Box } from "@curves/ui-base";
import BatchPaymentSearchBar from "@icoach/batchPayment/BatchPaymentSearchBar";
import MembershipBatchPaymentPage from "@icoach/batchPayment/membershipBatchPayment/MembershipBatchPaymentPage";
import RentCabinetBatchPaymentPage from "@icoach/batchPayment/rentCabinetBatchPayment/RentCabinetBatchPaymentPage";
import CyclePurchaseBatchPaymentPage from "@icoach/batchPayment/cyclePurchaseBatchPayment/CyclePurchaseBatchPaymentPage";
import AccountBookDialogBox from "@icoach/accountBook/dialog/AccountBookDialogBox";
import useOrderBatchPaymentApi from "@apis/useOrderBatchPaymentApi";
import { BatchPaymentProvider } from "@icoach/batchPayment/component/BatchPaymentContext";
import { isEmpty, isEqual } from "@util/utils";
import { parseDate } from "@util/moment";
import useOptions from "@util/hook/useOptions";
import { BATCH_PAYMENT_TYPE } from "@icoach/batchPayment/staticData";
import useQueryManager from "@util/hook/useQueryManager";
import SmartFitnessBandBatchPaymentPage from "@icoach/batchPayment/smartFitnessBand/SmartFitnessBandBatchPaymentPage";

const useBatchPayments = ({ getBatchPaymentApi, defaultSearchParams = {} }) => {
	const { searchParams, setQuery } = useQueryManager();
	const [batchPaymentLists, setBatchPaymentLists] = useState(null);
	// replaceState 不觸發 useLocation 更新，搜尋後 searchParams 會是舊值
	// 用獨立 state 追蹤最後一次 API 呼叫的 type（供列表 render 判斷）與完整 filters（供子頁面建構 tab URL）
	const [paymentType, setPaymentType] = useState(null);
	const [lastSearchFilters, setLastSearchFilters] = useState(null);
	const preSearchParamsRef = useRef(null);

	// 取得批次扣款資料
	const doGetBatchPaymentApi = async ({ type, date }) => {
		try {
			const resp = await getBatchPaymentApi({ type, date });
			if (resp) {
				setBatchPaymentLists(resp);
				setPaymentType(type);
				setLastSearchFilters({ type, date });
			}
		} catch (error) {
			console.error("Error fetching batch payment data:", error);
		}
	};

	// 取得批次扣款資料,並更新 searchParams
	const getBatchPayments = filters => {
		doGetBatchPaymentApi(filters);
		setQuery(filters);
	};

	// 清空批次扣款資料
	const resetBatchPayments = () => {
		if (!isEmpty(batchPaymentLists)) {
			setBatchPaymentLists(null);
			setPaymentType(null);
			setLastSearchFilters(null);
		}
	};

	// 設定 searchParams
	useEffect(
		() => {
			if ((!isEmpty(searchParams) || !isEmpty(defaultSearchParams)) && !isEqual(preSearchParamsRef.current, searchParams)) {
				setQuery(isEmpty(searchParams) ? defaultSearchParams : searchParams);
			}

			preSearchParamsRef.current = searchParams;
		},
		// eslint-disable-next-line
		[defaultSearchParams, searchParams],
	);

	return {
		searchParams,
		paymentType,
		lastSearchFilters,
		batchPaymentLists,
		getBatchPayments,
		resetBatchPayments,
	};
};

const BatchPaymentPage = () => {
	const { getOrderBatchPaymentOptionsApi, getOrderBatchPaymentApi } = useOrderBatchPaymentApi();
	const { options } = useOptions({ getOptionsApi: getOrderBatchPaymentOptionsApi, isFetchOnMount: true });
	const { searchParams, paymentType, lastSearchFilters, batchPaymentLists, getBatchPayments, resetBatchPayments } = useBatchPayments({
		getBatchPaymentApi: getOrderBatchPaymentApi,
		defaultSearchParams: {
			type: 1,
			date: parseDate(),
		},
	});

	// lastSearchFilters 提供正確的 type/date（不受 replaceState stale 影響）
	// searchParams 提供 main-tab/sub-tab（React Router Link 導航後由 useLocation 更新）
	const effectiveSearchParams = { ...searchParams, ...lastSearchFilters };

	const dialogRef = useRef(null);

	const handleSearch = params => getBatchPayments(params);

	return (
		<BatchPaymentProvider value={{ searchParams: effectiveSearchParams, batchPaymentLists, getBatchPayments, resetBatchPayments, dialogRef }}>
			<Box className={"batch-payment-page"}>
				<BatchPaymentSearchBar searchParams={effectiveSearchParams} options={options} resetBatchPayments={resetBatchPayments} onSubmit={handleSearch} />
				{!isEmpty(batchPaymentLists) ? (
					<>
						{isEqual(paymentType, BATCH_PAYMENT_TYPE.MEMBERSHIP) && <MembershipBatchPaymentPage searchParams={effectiveSearchParams} />}
						{isEqual(paymentType, BATCH_PAYMENT_TYPE.RENT_CABINET) && <RentCabinetBatchPaymentPage searchParams={effectiveSearchParams} />}
						{isEqual(paymentType, BATCH_PAYMENT_TYPE.CYCLE_PURCHASE) && <CyclePurchaseBatchPaymentPage searchParams={effectiveSearchParams} />}
						{isEqual(paymentType, BATCH_PAYMENT_TYPE.SMART_FITNESS_BAND) && <SmartFitnessBandBatchPaymentPage searchParams={effectiveSearchParams} />}
					</>
				) : null}
				<AccountBookDialogBox ref={dialogRef} />
			</Box>
		</BatchPaymentProvider>
	);
};

export default BatchPaymentPage;
